import type { FileItem } from "../types/file-item.types.ts"
import type { FilesSortOrder } from "../types/files-filters.types.ts"

/** Turns whatever the API sent for a date into something comparable. */
export type ParseSortDate = (value: string) => number

interface Keyed {
  item: FileItem
  created: number
  updated: number
}

const newestModified = (a: Keyed, b: Keyed) => b.updated - a.updated

const compareBy = (order: FilesSortOrder): ((a: Keyed, b: Keyed) => number) => {
  switch (order) {
    case "added_desc":
      return (a, b) => b.created - a.created
    case "name_asc":
      return (a, b) => a.item.name.localeCompare(b.item.name)
    case "name_desc":
      return (a, b) => b.item.name.localeCompare(a.item.name)
    case "size_desc":
      return (a, b) => (b.item.fileSize ?? 0) - (a.item.fileSize ?? 0)
    case "size_asc":
      return (a, b) => (a.item.fileSize ?? 0) - (b.item.fileSize ?? 0)
    case "modified_desc":
    default:
      return newestModified
  }
}

/** Client-side because album files and documents come from two endpoints. A tie
 * goes to the most recently modified, then keeps the input order: for album files
 * uploaded in one minute, that order is the only record of which came last. */
export const sortFileItems = (
  items: FileItem[],
  order: FilesSortOrder,
  parseDate: ParseSortDate
): FileItem[] => {
  const compare = compareBy(order)
  return items
    .map((item) => ({
      item,
      created: parseDate(item.created),
      updated: parseDate(item.updated)
    }))
    .sort((a, b) => compare(a, b) || newestModified(a, b))
    .map(({ item }) => item)
}
